/**
 * Today's actions on a roster row write HELM's record (A7Y-5210).
 *
 * A roster row on the card is a `HelmCommitment` whose evidence names the
 * record's roster item (`evidence.ref.kind === 'roster_row'`, `ref.id` the
 * item id since A7Y-4977) and, when the item has an account, links its account
 * page (`…/admin/cmo/crm/<acct-id>`). For such a row Done closes the item with
 * a route and an outcome, Defer re-dates it, and Waiting on sets the account's
 * watch and re-dates the item to the recheck day. Every other card item keeps
 * HELM's own overlay (`helm_actions`) and nothing more.
 */
import { createContext, useContext } from 'react';
import type { HelmActionInput, HelmCommitment, HelmFacts } from '@a7y/platform-health/helm';
import { api } from '@/lib/api';

/** The record's roster item id rule (`RECORD_ID_RE` in `crm_domain.ts`); a markdown-era row id is `<table>:<line>`. */
const RECORD_ID_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/;
const ACCOUNT_PAGE_RE = /\/admin\/cmo\/crm\/(acct-[a-z0-9]+(?:-[a-z0-9]+)*)\/?(?:[?#].*)?$/;

export interface RecordTarget {
  /** The record's roster item. */
  rosterItemId: string;
  /** The item's account, when the row links one. */
  accountId: string | null;
}

/** The roster item a card row is, or null when the row is not one (or predates the record). */
export function recordTargetOf(commitment: HelmCommitment): RecordTarget | null {
  const { ref } = commitment.evidence;
  if (ref.kind !== 'roster_row' || !RECORD_ID_RE.test(ref.id)) return null;
  const account = ref.url ? ACCOUNT_PAGE_RE.exec(ref.url) : null;
  return { rosterItemId: ref.id, accountId: account ? account[1] : null };
}

/** `item key → roster item` over a snapshot's open roster rows. */
export function buildRecordIndex(facts: HelmFacts): Map<string, RecordTarget> {
  const index = new Map<string, RecordTarget>();
  for (const c of facts.commitments) {
    if (c.table === 'closed') continue;
    const target = recordTargetOf(c);
    if (target) index.set(c.key, target);
  }
  return index;
}

/** What Done asks for on a roster row: the record's close needs both. */
export interface CloseDetails {
  route: string;
  outcome: string;
}

/** The account's watch text for a Waiting on. */
export function watchTextFor(input: HelmActionInput): string {
  const side = input.waitingSide === 'internal' ? 'us' : 'them';
  const recheck = input.recheckOn ? `, recheck ${input.recheckOn}` : '';
  return `Waiting on ${input.waitingOn ?? ''} (${side})${recheck}`;
}

/** Writes one Today action into the record. Rejects with the API's error; nothing is written by the caller after a rejection. */
export async function writeRecordAction(
  target: RecordTarget,
  input: HelmActionInput,
  close: CloseDetails | null,
  today: string
): Promise<void> {
  if (input.kind === 'done') {
    if (!close) throw new Error('Done on a roster row needs a route and an outcome');
    await api.closeCrmRosterItem(target.rosterItemId, {
      route: close.route,
      outcome: close.outcome,
      closedOn: today,
    });
    return;
  }
  if (input.kind === 'defer') {
    if (!input.deferUntil) throw new Error('Defer needs a date');
    await api.redateCrmRosterItem(target.rosterItemId, input.deferUntil);
    return;
  }
  if (input.kind === 'waiting_on') {
    if (target.accountId) await api.setCrmWatch(target.accountId, watchTextFor(input));
    if (input.recheckOn) await api.redateCrmRosterItem(target.rosterItemId, input.recheckOn);
    return;
  }
  throw new Error(`${input.kind} is not a record action`);
}

/** The record actions `ItemActionsMenu` offers on a roster row; provided by `HelmPage` for today's run only. */
export interface HelmRecordActions {
  targets: Map<string, RecordTarget>;
  onRecordAction: (
    target: RecordTarget,
    input: HelmActionInput,
    close: CloseDetails | null
  ) => void;
}

export const HelmRecordContext = createContext<HelmRecordActions | null>(null);

export function useHelmRecordActions(): HelmRecordActions | null {
  return useContext(HelmRecordContext);
}
